import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatUsd } from '../lib/api';
import { AwsLink, Button, Card, Alert, Field, Select, Input } from './ui';

/**
 * Recording an encoder or a flow to S3 (server: services/recorder.ts).
 *
 * The recorder is its own small channel on the same feed, so starting and
 * stopping a recording touches nothing else. What it records appears in the
 * Recordings tab, where it is played, trimmed, downloaded and shared.
 */

interface RecorderSpec { resolution: string; fps: string; mbps: number; audioPairs: number[]; audioKbps: number; transcribe?: boolean }
interface Recorder {
  exists: boolean;
  state: string; progress: string | null; error: string | null; busy: boolean;
  spec: RecorderSpec; audioPairs: number; ownerLive: boolean;
  startedAt: string | null; stopAt: string | null;
  /** A recording asked for ahead of time: when it starts (once the owner runs), for how long. */
  startAt: string | null; startStopAfterHours: number | null;
  session: { instanceId: string; session: string; prefix: string } | null;
  rate: { perHour: number; idlePerHour: number; unpriced: boolean; gbPerHour: number } | null;
  spend: { hours: number; usd: number };
  /** The recorder channel's AWS console page, once it has one. */
  console?: string | null;
  retentionDays: number;
  options: { resolutions: string[]; frameRates: string[]; audioBitrates: number[] };
}

const elapsed = (from: string) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(from).getTime()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${String(s % 60).padStart(2, '0')}`;
};

export function RecorderCard({ kind, id, canUse, canEdit }: { kind: 'encoder' | 'flow'; id: string; canUse: boolean; canEdit: boolean }) {
  const [rec, setRec] = useState<Recorder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [draft, setDraft] = useState<RecorderSpec | null>(null);
  const [hours, setHours] = useState('');
  // Start now, or at a time (datetime-local, the browser's zone).
  const [when, setWhen] = useState<'now' | 'at'>('now');
  const [at, setAt] = useState('');
  const [, tick] = useState(0);

  const load = useCallback(() =>
    api.get<{ recorder: Recorder }>(`/recorders/${kind}/${id}`).then((r) => setRec(r.recorder)).catch(() => undefined), [kind, id]);

  const moving = !!rec && (rec.busy || ['creating', 'starting', 'stopping', 'deleting'].includes(rec.state));
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), moving ? 4000 : 12000);
    return () => clearInterval(t);
  }, [load, moving]);
  // The running clock.
  useEffect(() => {
    if (rec?.state !== 'recording') return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [rec?.state]);

  if (!rec) return null;

  const act = async (fn: () => Promise<unknown>) => {
    setActing(true); setError(null);
    try { await fn(); await load(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'That did not work.'); }
    finally { setActing(false); }
  };

  const spec = draft ?? rec.spec;
  const recording = rec.state === 'recording';
  const idle = ['none', 'stopped', 'failed'].includes(rec.state) && !rec.busy;
  const recLink = rec.session ? `/recordings?open=${rec.session.instanceId}/${rec.session.session}` : null;

  return (
    <Card
      title="Recording"
      action={
        <div className="flex items-center gap-2">
          <AwsLink href={rec.console} what="channel" />
          {recording && canUse && (
            <Button loading={acting} onClick={() => void act(() => api.post(`/recorders/${kind}/${id}/stop`))}>Stop recording</Button>
          )}
          {idle && canUse && rec.startAt && (
            <Button loading={acting} onClick={() => void act(() => api.delete(`/recorders/${kind}/${id}/schedule`))}>Cancel the scheduled recording</Button>
          )}
          {idle && canUse && !rec.startAt && (
            <Button
              variant="primary" loading={acting} disabled={when === 'at' && !at}
              title={when === 'at' ? 'The hub starts the recording at that time, once the ' + kind + ' is running' : rec.ownerLive ? undefined : `The ${kind} is not running: the recording starts as soon as it is`}
              onClick={() => void act(async () => {
                if (when === 'at' || !rec.ownerLive) {
                  await api.post(`/recorders/${kind}/${id}/schedule`, { spec: draft ?? undefined, startAt: when === 'at' ? new Date(at).toISOString() : null, stopAfterHours: hours ? Number(hours) : null });
                } else {
                  await api.post(`/recorders/${kind}/${id}/start`, { spec: draft ?? undefined, stopAfterHours: hours ? Number(hours) : null });
                }
                setDraft(null); setHours(''); setWhen('now'); setAt('');
              })}
            >
              {when === 'at' ? '● Record at that time' : rec.ownerLive ? '● Record' : '● Record when it runs'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-3 p-4 text-sm">
        {error && <Alert tone="critical">{error}</Alert>}
        {rec.error && <Alert tone="critical">{rec.error}</Alert>}

        {recording && rec.startedAt && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium" style={{ color: 'var(--color-critical)' }}>● Recording {elapsed(rec.startedAt)}</span>
            <span className="text-ink-muted">{rec.spec.resolution}{rec.spec.fps} · {rec.spec.mbps} Mbit · {rec.spec.audioPairs.length > 1 ? `all ${rec.spec.audioPairs.length} pairs` : 'pair 1'}{rec.spec.transcribe ? ' · transcribing' : ''}</span>
            {canUse && (
              <label className="flex items-center gap-1.5 text-xs text-ink-muted">
                {rec.stopAt ? `Stops ${new Date(rec.stopAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Stop timer'}
                <select
                  className="rounded border border-grid bg-plane px-1 py-1 text-xs text-ink" value=""
                  onChange={(e) => { const v = e.target.value; if (v !== '') void act(() => api.post(`/recorders/${kind}/${id}/timer`, { hours: Number(v) })); }}
                >
                  <option value="">{rec.stopAt ? 'change…' : 'never'}</option>
                  {[1, 2, 3, 4, 6, 8, 12].map((h) => <option key={h} value={h}>in {h} h</option>)}
                  {rec.stopAt && <option value="0">never</option>}
                </select>
              </label>
            )}
          </div>
        )}
        {moving && <div className="text-ink-secondary">{rec.progress ?? 'Working…'}</div>}
        {idle && rec.startAt && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium text-warning">
              ● Recording scheduled: {new Date(rec.startAt).getTime() <= Date.now() ? `starts as soon as the ${kind} is running` : `starts ${new Date(rec.startAt).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`}
              {rec.startStopAfterHours ? `, stops after ${rec.startStopAfterHours} h` : ''}
            </span>
            <span className="text-ink-muted">{rec.spec.resolution}{rec.spec.fps} · {rec.spec.mbps} Mbit · pair{rec.spec.audioPairs.length === 1 ? '' : 's'} {rec.spec.audioPairs.join(', ')}{rec.spec.transcribe ? ' · transcribing' : ''}</span>
            {!rec.ownerLive && <span className="text-xs text-ink-muted">The hub waits up to two hours past the time for the {kind} to be running.</span>}
          </div>
        )}

        {idle && !rec.startAt && (
          <>
            <p className="text-ink-secondary">
              Records what arrives to S3, on its own small channel - starting and stopping it touches nothing else.
              Recordings are kept {rec.retentionDays} days and appear in the Recordings tab, to play, trim, download or share with a link.
              {!rec.exists && ' The first recording takes about two minutes to start; later ones about one.'}
            </p>
            {canUse && (
              <div className="grid gap-3 sm:grid-cols-5">
                <Field label="Resolution">
                  <Select value={spec.resolution} onChange={(e) => setDraft({ ...spec, resolution: e.target.value })}>
                    {rec.options.resolutions.map((r) => <option key={r}>{r}</option>)}
                  </Select>
                </Field>
                <Field label="Frame rate">
                  <Select value={spec.fps} onChange={(e) => setDraft({ ...spec, fps: e.target.value })}>
                    {rec.options.frameRates.map((r) => <option key={r}>{r}</option>)}
                  </Select>
                </Field>
                <Field label="Bitrate (Mbit)">
                  <Input type="number" min={1} max={20} step={0.5} value={spec.mbps} onChange={(e) => setDraft({ ...spec, mbps: Number(e.target.value) })} />
                </Field>
                <Field label="Audio" hint="Every pair is its own track in the file.">
                  <Select
                    value={spec.audioPairs.length > 1 ? 'all' : 'one'}
                    onChange={(e) => setDraft({ ...spec, audioPairs: e.target.value === 'all' ? Array.from({ length: rec.audioPairs }, (_, i) => i + 1) : [1] })}
                  >
                    <option value="one">Pair 1 only</option>
                    <option value="all" disabled={rec.audioPairs < 2}>All {rec.audioPairs} pairs</option>
                  </Select>
                </Field>
                <Field label="Stop after">
                  <Select value={hours} onChange={(e) => setHours(e.target.value)}>
                    <option value="">never</option>
                    {[1, 2, 3, 4, 6, 8, 12].map((h) => <option key={h} value={h}>{h} h</option>)}
                  </Select>
                </Field>
                <Field label="Start">
                  <Select value={when} onChange={(e) => setWhen(e.target.value as 'now' | 'at')}>
                    <option value="now">{rec.ownerLive ? 'now' : `when the ${kind} is running`}</option>
                    <option value="at">at a time…</option>
                  </Select>
                </Field>
                {when === 'at' && (
                  <Field label="Start time" hint="Your computer's time zone.">
                    <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
                  </Field>
                )}
                <Field label="Transcript" hint="Amazon Transcribe, about US$1.44 an hour. Can also be asked for later, on the Recordings page.">
                  <label className="flex items-center gap-1.5 pt-1.5 text-xs">
                    <input type="checkbox" checked={!!spec.transcribe} onChange={(e) => setDraft({ ...spec, transcribe: e.target.checked })} />
                    Transcribe what is said
                  </label>
                </Field>
              </div>
            )}
            {draft && <p className="text-xs text-ink-muted">The cost below is for the saved settings; it is priced again when the recording starts.</p>}
          </>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
          {rec.rate && (
            <span>
              {formatUsd(rec.rate.perHour)}/h while recording{rec.rate.unpriced ? ' (part unpriced)' : ''} · about {rec.rate.gbPerHour} GB an hour
              {rec.exists && ` · ${formatUsd(rec.rate.idlePerHour)}/h kept idle`}
            </span>
          )}
          {rec.spend.hours > 0 && <span>{rec.spend.hours.toFixed(1)} h recorded, about {formatUsd(rec.spend.usd)}</span>}
          {recLink && <Link className="text-series-1 hover:underline" to={recLink}>{recording ? 'Watch it in Recordings →' : 'Last recording →'}</Link>}
          <Link className="text-series-1 hover:underline" to="/recordings">All recordings</Link>
          {rec.exists && idle && canEdit && (
            <button className="text-series-1 hover:underline" disabled={acting} onClick={() => void act(() => api.delete(`/recorders/${kind}/${id}`))}>
              Remove the recorder
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
